import { INACTIVE_PROMOTION_TASK, MISSING_PROMOTION_TASK } from './constants';

/** A product identifier is "empty" when it is null, blank, or the placeholder 0. */
export function isEmptyIdentifier(value) {
  if (value === null || value === undefined) {
    return true;
  }
  const s = String(value).trim();
  return s === '' || s === '0';
}

/**
 * Resolves the operational item/ean for a task applying the promotion fallback:
 * inactive/missing promotion tasks whose primary item or ean is empty or the
 * placeholder 0 fall back to store_info.associated_item / associated_ean.
 *
 * This mirrors the rule used by the task details display so search and
 * visualization stay consistent from a single source.
 *
 * @param {object} task - Task/alert information object.
 * @returns {{ item: any, ean: any }} The resolved identifiers.
 */
export function resolveTaskIdentifiers(task) {
  const isPromotion =
    task?.task_type === INACTIVE_PROMOTION_TASK ||
    task?.task_type === MISSING_PROMOTION_TASK;

  if (!isPromotion) {
    return { item: task?.item, ean: task?.ean };
  }

  const item = !isEmptyIdentifier(task?.item)
    ? task.item
    : task?.store_info?.associated_item;
  const ean = !isEmptyIdentifier(task?.ean)
    ? task.ean
    : task?.store_info?.associated_ean;

  return { item, ean };
}

/**
 * Returns the uppercased identifier strings a task should be searchable by
 * (resolved item and ean), skipping empty/placeholder values. Keeping this in
 * sync with resolveTaskIdentifiers lets store teams find promotion tasks by the
 * associated barcode shown in the details view.
 *
 * @param {object} task - Task/alert information object.
 * @returns {string[]} Non-empty, uppercased identifier strings.
 */
export function getSearchableIdentifiers(task) {
  const { item, ean } = resolveTaskIdentifiers(task);
  const values = [];
  if (!isEmptyIdentifier(item)) {
    values.push(String(item).toUpperCase());
  }
  if (!isEmptyIdentifier(ean)) {
    values.push(String(ean).toUpperCase());
  }
  return values;
}

/**
 * Returns the precomputed inactive reason for an inactive-promotion task.
 *
 * The reason is read straight from store_info.inactive_reason (single source of
 * truth). No client-side computation: if the value is absent the caller should
 * render nothing.
 *
 * @param {object} task - Task/alert information object.
 * @returns {(string|null)} The inactive_reason value, or null when the task is
 *                          not an inactive promotion or the value is missing.
 */
export function resolveInactivePromotionReason(task) {
  if (task?.task_type !== INACTIVE_PROMOTION_TASK) {
    return null;
  }
  return task?.store_info?.inactive_reason ?? null;
}
